import { useState, useCallback, useEffect, useRef } from 'react';
import { Geolocation, PositionOptions } from '@capacitor/geolocation';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { App } from '@capacitor/app';
import { db, rtdb } from '../firebase/client';
import { doc, updateDoc, serverTimestamp as firestoreTimestamp } from 'firebase/firestore';
import { ref, update, serverTimestamp as rtdbTimestamp } from 'firebase/database';
import { Network } from '@capacitor/network';

// Import background geolocation community plugin
// We use registerPlugin to avoid build errors if the dev hasn't synced yet
const BackgroundGeolocation = registerPlugin<any>('BackgroundGeolocation');

// Throttling Constants
const DISTANCE_THRESHOLD_FIRESTORE = 200; // meters
const TIME_THRESHOLD_FIRESTORE = 5 * 60 * 1000; // 5 minutes

const DISTANCE_THRESHOLD_RTDB = 50; // meters
const TIME_THRESHOLD_RTDB = 10 * 1000; // 10 seconds

const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

export const useGeolocation = (userId?: string) => {
    const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
    const locationRef = useRef<{ lat: number; lng: number } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<number | null>(null);
    const [isTracking, setIsTracking] = useState(false);
    const [lastSyncTimestamp, setLastSyncTimestamp] = useState<number | null>(null);
    const [isOnline, setIsOnline] = useState(true);
    
    // Watchers
    const watchIdRef = useRef<string | null>(null);
    const backgroundWatcherIdRef = useRef<string | null>(null);
    const activeOrderIdRef = useRef<string | undefined>(undefined);

    const lastFirestoreUpdate = useRef<{ loc: { lat: number, lng: number }, time: number } | null>(null);
    const lastRTDBUpdate = useRef<{ loc: { lat: number, lng: number }, time: number } | null>(null);
    
    // Improved Location Queue for Offline Persistence
    const locationQueueRef = useRef<{ lat: number, lng: number, time: number, orderId?: string }[]>([]);
    const isFlushingRef = useRef(false);

    const wakeLockRef = useRef<any>(null);

    const requestWakeLock = async () => {
        if ('wakeLock' in navigator) {
            try {
                wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
                console.log('🔓 Wake Lock active.');
            } catch (err: any) {
                console.warn(`Wake Lock failed: ${err.message}`);
            }
        }
    };

    const releaseWakeLock = async () => {
        if (wakeLockRef.current) {
            await wakeLockRef.current.release();
            wakeLockRef.current = null;
        }
    };

    const requestPermissions = async () => {
        try {
            // Web platform doesn't need explicit requestPermissions (handled by getCurrentPosition)
            if (Capacitor.getPlatform() === 'web') return true;

            console.log("📍 [Native] Triggering Capacitor Geolocation requestPermissions...");
            const permission = await Geolocation.requestPermissions();
            
            console.log("📍 [Native] Permission result:", permission.location);

            if (permission.location !== 'granted' && permission.location !== 'prompt') {
                setError('Location permission denied');
                return false;
            }
            return true;
        } catch (err: any) {
            console.error("📍 [Native] Error in requestPermissions:", err);
            // If the plugin is not implemented, we might be on a dev browser spoofing native
            if (err.message?.includes('not implemented')) return true;
            setError(err.message || 'Error requesting permissions');
            return false;
        }
    };

    const flushLocationQueue = async (uid: string) => {
        if (locationQueueRef.current.length === 0 || isFlushingRef.current) return;
        
        console.log(`📤 Flushing ${locationQueueRef.current.length} queued locations...`);
        isFlushingRef.current = true;
        
        try {
            const queue = [...locationQueueRef.current];
            // Sort by time to be sure
            queue.sort((a, b) => a.time - b.time);
            
            // Only push the 3 most recent points to avoid overloading on reconnection
            // But the very LAST one is essential.
            const pointsToPush = queue.slice(-3); 
            
            for (const item of pointsToPush) {
                // For Firestore, we just care about current position mostly, 
                // but RTDB needs the live update for the client.
                const userRef = doc(db, 'users', uid);
                await updateDoc(userRef, {
                    location: { lat: item.lat, lng: item.lng },
                    updatedAt: firestoreTimestamp()
                });

                if (item.orderId) {
                    const trackingRef = ref(rtdb, `tracking/${item.orderId}`);
                    await update(trackingRef, {
                        lat: item.lat,
                        lng: item.lng,
                        updatedAt: rtdbTimestamp(),
                    });
                }
            }
            
            // Clear successfully flushed points (or all if we only keep latest)
            locationQueueRef.current = [];
            console.log("✅ Location queue flushed.");
        } catch (err) {
            console.warn("Failed to flush location queue, will retry later:", err);
        } finally {
            isFlushingRef.current = false;
        }
    };

    const updateLocationInDb = async (uid: string, loc: { lat: number, lng: number }, activeOrderId?: string) => {
        const now = Date.now();
        const orderIdToUpdate = activeOrderId || activeOrderIdRef.current;

        try {
            // Check if we are online first
            const netStatus = await Network.getStatus();
            if (!netStatus.connected) {
                throw new Error("OFFLINE_MODE");
            }

            let shouldUpdateFirestore = false;
            if (!lastFirestoreUpdate.current) {
                shouldUpdateFirestore = true;
            } else {
                const dist = calculateDistance(
                    lastFirestoreUpdate.current.loc.lat,
                    lastFirestoreUpdate.current.loc.lng,
                    loc.lat,
                    loc.lng
                );
                const timeDiff = now - lastFirestoreUpdate.current.time;
                if (dist >= DISTANCE_THRESHOLD_FIRESTORE || timeDiff >= TIME_THRESHOLD_FIRESTORE) {
                    shouldUpdateFirestore = true;
                }
            }

            if (shouldUpdateFirestore) {
                const userRef = doc(db, 'users', uid);
                await updateDoc(userRef, {
                    location: loc,
                    updatedAt: firestoreTimestamp()
                });
                lastFirestoreUpdate.current = { loc, time: now };
            }

            if (orderIdToUpdate) {
                let shouldUpdateRTDB = false;
                if (!lastRTDBUpdate.current) {
                    shouldUpdateRTDB = true;
                } else {
                    const dist = calculateDistance(
                        lastRTDBUpdate.current.loc.lat,
                        lastRTDBUpdate.current.loc.lng,
                        loc.lat,
                        loc.lng
                    );
                    const timeDiff = now - lastRTDBUpdate.current.time;
                    if (dist >= DISTANCE_THRESHOLD_RTDB || timeDiff >= TIME_THRESHOLD_RTDB) {
                        shouldUpdateRTDB = true;
                    }
                }

                if (shouldUpdateRTDB) {
                    const trackingRef = ref(rtdb, `tracking/${orderIdToUpdate}`);
                    await update(trackingRef, {
                        lat: loc.lat,
                        lng: loc.lng,
                        updatedAt: rtdbTimestamp(),
                    });
                    lastRTDBUpdate.current = { loc, time: now };
                }
            }
            
            // If we successfully updated, try to flush any remaining items in queue
            if (locationQueueRef.current.length > 0) flushLocationQueue(uid);

        } catch (err: any) {
            console.log("📍 Location update deferred (Offline or Error):", err.message);
            // Append to queue
            locationQueueRef.current.push({
                ...loc,
                time: now,
                orderId: orderIdToUpdate
            });
            // Keep queue manageable
            if (locationQueueRef.current.length > 20) {
                locationQueueRef.current.shift();
            }
        }
    };

    const getCurrentPosition = useCallback(async (activeOrderId?: string) => {
        try {
            setErrorCode(null);
            setError(null);

            const position = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            });

            const newLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
            };

            setLocation(newLocation);
            locationRef.current = newLocation;

            if (userId) {
                await updateLocationInDb(userId, newLocation, activeOrderId);
            }

            setLastSyncTimestamp(Date.now());
            return newLocation;
        } catch (err: any) {
            console.error("❌ Geolocation Error:", err);
            const code = err.code || (err.message?.includes('denied') ? 1 : null);
            setErrorCode(code);
            setError(err.message || 'Error getting location');
            return null;
        }
    }, [userId, updateLocationInDb]);

    const startTracking = useCallback(async (activeOrderId?: string) => {
        activeOrderIdRef.current = activeOrderId;
        requestWakeLock();

        if (activeOrderId && userId && locationRef.current) {
            updateLocationInDb(userId, locationRef.current, activeOrderId);
        }

        if (userId) getCurrentPosition(activeOrderId);

        if (isTracking || watchIdRef.current) return;

        try {
            const hasPermission = await requestPermissions();
            if (!hasPermission) return;

            // 1. Standard Watcher (Mainly for foreground & PWA)
            const id = await Geolocation.watchPosition({
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0
            }, async (position, err) => {
                if (err) {
                    console.warn("Standard Watch error:", err.message);
                    return;
                }
                if (position) {
                    const newLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
                    setLocation(newLocation);
                    locationRef.current = newLocation;
                    if (userId) await updateLocationInDb(userId, newLocation, activeOrderIdRef.current);
                }
            });
            watchIdRef.current = id;

            // 2. Native Background Watcher (Only for iOS/Android App)
            if (Capacitor.isNativePlatform()) {
                console.log("🛠 Starting Native Background Geolocation...");
                try {
                    const bgId = await BackgroundGeolocation.addWatcher(
                        {
                            backgroundMessage: "Suivi de livraison en cours (cliquez pour ouvrir)",
                            backgroundTitle: "Yumii - Service Livreur Actif",
                            requestPermissions: true,
                            stale: false,
                            distanceFilter: 30 
                        },
                        async (result: any, err: any) => {
                            if (err) {
                                console.error("Background Watch error:", err);
                                return;
                            }
                            if (result) {
                                const newLocation = { lat: result.latitude, lng: result.longitude };
                                setLocation(newLocation);
                                locationRef.current = newLocation;
                                if (userId) {
                                    // Direct call to update - will queue if offline
                                    updateLocationInDb(userId, newLocation, activeOrderIdRef.current);
                                }
                            }
                        }
                    );
                    backgroundWatcherIdRef.current = bgId;
                } catch (bgErr) {
                    console.warn("Background Geolocation not available or failed:", bgErr);
                }
            }

            setIsTracking(true);
        } catch (err: any) {
            setError(err.message || 'Error starting tracking');
        }
    }, [userId, isTracking, getCurrentPosition]);

    const stopTracking = useCallback(async () => {
        releaseWakeLock();
        if (watchIdRef.current) {
            await Geolocation.clearWatch({ id: watchIdRef.current });
            watchIdRef.current = null;
        }
        if (backgroundWatcherIdRef.current && Capacitor.isNativePlatform()) {
            try {
                await BackgroundGeolocation.removeWatcher({ id: backgroundWatcherIdRef.current });
                backgroundWatcherIdRef.current = null;
            } catch (err) {
                console.warn("Error removing background watcher:", err);
            }
        }
        setIsTracking(false);
    }, []);

    // Handle App Visibility and State Changes (Hybrid)
    useEffect(() => {
        const handleResumedSync = async () => {
            console.log("🔄 App ACTIVE/RESUMED. Forcing GPS sync...");
            if (isTracking && userId) {
                try {
                    await getCurrentPosition(activeOrderIdRef.current);
                    requestWakeLock();
                } catch (err) {
                    console.error("Resume sync error:", err);
                }
            }
        };

        // 1. Web visibility change (PWA)
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') handleResumedSync();
        };
        window.addEventListener('visibilitychange', onVisibilityChange);

        // 2. Capacitor App State Change (Native App - more robust)
        const appStateListener = App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) {
                handleResumedSync();
            } else {
                console.log("💤 App in background. Relying on background service...");
            }
        });

        return () => {
            window.removeEventListener('visibilitychange', onVisibilityChange);
            appStateListener.then(l => l.remove());
        };
    }, [isTracking, userId, getCurrentPosition]);

    // Network awareness to force sync on reconnection
    useEffect(() => {
        const networkListener = Network.addListener('networkStatusChange', (status) => {
            console.log("🕸 Network status changed:", status.connected ? 'ONLINE' : 'OFFLINE');
            setIsOnline(status.connected);
            
            if (status.connected && isTracking) {
                console.log("🚀 Connection RESTORED. Flushing queue and syncing GPS...");
                setTimeout(() => {
                    getCurrentPosition(activeOrderIdRef.current);
                    if (userId) flushLocationQueue(userId);
                }, 3000); // 3s delay to ensure socket stability
            }
        });

        // Initial check
        Network.getStatus().then(status => setIsOnline(status.connected));

        return () => {
            networkListener.then(l => l.remove());
        };
    }, [isTracking, userId, getCurrentPosition]);

    const checkPermissions = async () => {
        try {
            if (Capacitor.getPlatform() === 'web') {
                const permission = await (navigator as any).permissions.query({ name: 'geolocation' });
                return permission.state;
            }
            const permission = await Geolocation.checkPermissions();
            return permission.location;
        } catch (err) {
            return 'prompt';
        }
    };

    return { 
        location, 
        error, 
        errorCode, 
        isPermissionError: errorCode === 1,
        requestPermissions, 
        checkPermissions, 
        getCurrentPosition, 
        startTracking,
        stopTracking, 
        isTracking,
        lastSyncTimestamp,
        isOnline
    };
};

